/**
 * Helpers for SchoolFinderV3 extension-field values served on `school.annual`.
 *
 * BE storage / wire format:
 *  - Multi-select: JSON-encoded string `'["Baseball","Soccer"]'` (sometimes a
 *    bare comma-separated string when admins paste values).
 *  - Yes/no: `'1'` / `'0'` strings, occasionally `true`/`false`.
 *  - Text / rich text: plain strings (rich text fields may include simple HTML
 *    that we render as text only — no dangerouslySetInnerHTML).
 *
 * Keep the runtime narrowing here so call sites stay declarative.
 */

/** SchoolFinderV3 `field_name` for the school-type choice field. */
export const SCHOOL_TYPE_FIELD = 'school_type' as const;

/** Yes/no field types stored as `'1'`/`'0'`. */
export const BOOL_FIELD_TYPES = new Set(['yes_no', 'yesno', 'boolean']);

// Placeholder values admins paste to mean "nothing here" — including the
// hyphen and the en/em dashes — are treated as empty so they collapse away.
const FALSY_TEXT = new Set(['', 'n/a', 'na', 'none', '-', '–', '—', 'null', 'undefined']);

// Collapse spacing so "Basketball  Boys" and "Basketball Boys" dedupe as one.
const normalizeToken = (v: unknown): string => String(v).trim().replace(/\s+/g, ' ');

export const parseFinderList = (raw: unknown): string[] => {
  if (Array.isArray(raw)) {
    return raw.map(normalizeToken).filter((v) => v && !FALSY_TEXT.has(v.toLowerCase()));
  }
  if (typeof raw !== 'string') return [];
  const trimmed = raw.trim();
  if (!trimmed) return [];

  if (trimmed.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map(normalizeToken)
          .filter((v) => v && !FALSY_TEXT.has(v.toLowerCase()));
      }
    } catch {
      /* fall through to comma-split */
    }
  }

  return trimmed
    .split(',')
    .map(normalizeToken)
    .filter((s) => s && !FALSY_TEXT.has(s.toLowerCase()));
};

export const parseFinderBool = (raw: unknown): boolean | null => {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'number') return raw !== 0;
  if (typeof raw === 'string') {
    const v = raw.trim().toLowerCase();
    if (!v || FALSY_TEXT.has(v)) return null;
    if (['1', 'true', 'yes', 'y'].includes(v)) return true;
    if (['0', 'false', 'no', 'n'].includes(v)) return false;
  }
  return null;
};

export const finderText = (raw: unknown): string | null => {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (FALSY_TEXT.has(trimmed.toLowerCase())) return null;
  return trimmed;
};

/**
 * Option values arrive as compact identifiers (`"DualCredit"`, `"APBiology"`).
 * Insert spaces between word boundaries so chip labels read like the admin form
 * options without requiring a join against the form-field option map.
 */
export const humanizeOption = (value: string): string => value
  .replace(/[_-]+/g, ' ')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
  .replace(/\s+/g, ' ')
  .trim();
